/**
 * The device page's hardware spec sheet, as lines of text.
 *
 * The port of the Overview-tab half of `DeviceDetailViewModel`, and it is a module of its own for
 * the reason that file gives: **every one of these lines is a list of facts that may be absent.** A
 * Synology reports no display adapter, a soldered-memory laptop reports no module serial, a VM
 * reports no chassis. Composing each line from the parts that exist - and returning '' when none do
 * - is what keeps a dangling " · " off the card, and it is why the page binds whole lines rather
 * than fragments.
 *
 * The rule each caller then follows: **an empty line hides its heading.** A card of empty headings
 * reads as data that failed to load, which is a different and much worse claim than "this machine
 * did not report that".
 */
import type {
  BatteryDto,
  DiskDto,
  GpuDto,
  HardwareInfoDto,
  MemoryModuleDto,
  MonitorDto,
  NicDto,
  TpmDto
} from './contracts.gen.js';
import { bytes, join, plainDate } from './format.js';

/**
 * Rendered rows are capped, as the XAML capped them.
 *
 * Sixty-four sits above any real machine's slot count, so the cap only ever bites on a hostile or
 * corrupt inventory - which the server already clamps, making this the second line of defence
 * rather than the first.
 */
export const MAX_HARDWARE_ROWS = 64;

/** `2.90 GHz` / `800 MHz`. */
export function clock(mhz: number): string {
  return mhz >= 1000 ? `${(mhz / 1000).toFixed(2)} GHz` : `${mhz} MHz`;
}

/** "Notebook · Serial ABC123", either half optional. */
export function chassisText(h: HardwareInfoDto | null): string {
  if (!h) return '';
  return join([h.chassisType, h.serialNumber ? `Serial ${h.serialNumber}` : null]);
}

/** The vendor's own family and SKU codes - what a warranty lookup actually wants. */
export function systemIdentityText(h: HardwareInfoDto | null): string {
  return h ? join([h.systemFamily, h.systemSku]) : '';
}

/** "16 cores / 16 threads · 2.90 GHz · Socket U3E1 · L3 24 MB". */
export function cpuDetailText(h: HardwareInfoDto | null): string {
  if (!h) return '';
  const parts: Array<string | null> = [];
  if (h.cpuCores > 0 || h.cpuLogical > 0) parts.push(`${h.cpuCores} cores / ${h.cpuLogical} threads`);
  if ((h.cpu?.sockets ?? 0) > 1) parts.push(`${h.cpu!.sockets} sockets`);
  if ((h.cpu?.maxClockMhz ?? 0) > 0) parts.push(clock(h.cpu!.maxClockMhz));
  if (h.cpu?.socket) parts.push(`Socket ${h.cpu.socket}`);
  if ((h.cpu?.l2CacheBytes ?? 0) > 0) parts.push(`L2 ${bytes(h.cpu!.l2CacheBytes)}`);
  if ((h.cpu?.l3CacheBytes ?? 0) > 0) parts.push(`L3 ${bytes(h.cpu!.l3CacheBytes)}`);
  return join(parts);
}

/**
 * "31.4 GB · 8 of 8 slots populated".
 *
 * THE SLOT PAIR IS THE POINT of listing memory by slot at all - it answers whether the machine can
 * take more RAM without discarding a stick - so it is stated even when every slot is full.
 */
export function memorySummaryText(h: HardwareInfoDto | null): string {
  if (!h) return '';
  return join([
    h.ramBytes > 0 ? bytes(h.ramBytes) : null,
    h.memorySlotsTotal > 0 ? `${h.memorySlotsUsed} of ${h.memorySlotsTotal} slots populated` : null
  ]);
}

export interface HardwareRow {
  readonly name: string;
  readonly detail: string;
  /** A third line, for the part number a module carries and soldered memory does not. */
  readonly extra?: string;
}

export function gpuRows(h: HardwareInfoDto | null): HardwareRow[] {
  return (h?.gpus ?? []).slice(0, MAX_HARDWARE_ROWS).map((g: GpuDto) => ({
    name: g.name,
    detail: join([
      g.vendor,
      g.memoryBytes > 0 ? bytes(g.memoryBytes) : null,
      // The driver date is a CALENDAR date, printed rather than converted - the same rule as the
      // BIOS release date and the invoice due date.
      g.driverVersion
        ? `driver ${g.driverVersion}${g.driverDateUtc ? ` (${plainDate(g.driverDateUtc)})` : ''}`
        : null
    ]),
    // The video processor repeats the adapter name on nearly every card; it is only worth a line
    // when it says something the name does not (an OEM-rebadged part).
    extra: join([
      g.currentMode,
      g.videoProcessor.toLowerCase() === g.name.toLowerCase() ? null : g.videoProcessor
    ])
  }));
}

export function memoryRows(h: HardwareInfoDto | null): HardwareRow[] {
  return (h?.memoryModules ?? []).slice(0, MAX_HARDWARE_ROWS).map((m: MemoryModuleDto) => {
    // Rated speed, with the configured speed only when the board is running the module SLOWER than
    // its rating - the case worth noticing (mismatched sticks), and noise where they agree.
    let speed = m.speedMhz > 0 ? `${m.speedMhz} MHz` : null;
    if (speed && m.configuredSpeedMhz > 0 && m.configuredSpeedMhz !== m.speedMhz) {
      speed += ` (running at ${m.configuredSpeedMhz} MHz)`;
    }
    return {
      // The bank label is the FALLBACK, not an addition: boards label the socket one way or the
      // other, and printing "DIMM A1 / BANK 0" for one physical slot reads as two.
      name: m.slot || m.bank,
      detail: join([
        m.capacityBytes > 0 ? bytes(m.capacityBytes) : null,
        m.memoryType,
        m.formFactor,
        speed,
        m.manufacturer
      ]),
      extra: join([m.partNumber, m.serialNumber ? `SN ${m.serialNumber}` : null])
    };
  });
}

export function monitorRows(h: HardwareInfoDto | null): HardwareRow[] {
  return (h?.monitors ?? []).slice(0, MAX_HARDWARE_ROWS).map((m: MonitorDto) => {
    // EDID's friendly name is the model ("LG ULTRAGEAR"); a panel publishing none leaves the vendor
    // as the only thing to lead with.
    const name = m.model || m.manufacturer || 'Display';
    return {
      name,
      detail: join([
        // Only repeat the vendor when the name line is not already it.
        name === m.manufacturer ? null : m.manufacturer,
        m.diagonalInches > 0 ? `${round1(m.diagonalInches)}"` : null,
        m.connection,
        m.yearOfManufacture > 0 ? String(m.yearOfManufacture) : null,
        m.serialNumber ? `SN ${m.serialNumber}` : null
      ])
    };
  });
}

export interface BatteryRow extends HardwareRow {
  /** Null when either capacity is missing - "could not tell", not "0% health". */
  readonly healthPercent: number | null;
  readonly healthText: string;
  readonly tone: 'ok' | 'warn' | 'danger';
}

/**
 * Full-charge capacity against design capacity - the pack's real condition.
 *
 * Null rather than 0 when a capacity is missing, and deliberately **NOT clamped to 100**: a new pack
 * routinely charges a few percent past its rating, and clamping would quietly turn "this battery is
 * fine" into "this battery is exactly at its limit".
 */
export function batteryHealthPercent(designMwh: number, fullMwh: number): number | null {
  if (designMwh <= 0 || fullMwh <= 0) return null;
  return (fullMwh / designMwh) * 100;
}

export function batteryRows(h: HardwareInfoDto | null): BatteryRow[] {
  return (h?.batteries ?? []).slice(0, MAX_HARDWARE_ROWS).map((b: BatteryDto) => {
    const health = batteryHealthPercent(
      b.designCapacityMilliwattHours,
      b.fullChargeCapacityMilliwattHours
    );
    // Green at 80 and up, amber to 50, red below - the thresholds a failing pack is replaced on,
    // and the same three-band shape the temperature and volume readings use.
    const tone: 'ok' | 'warn' | 'danger' =
      health === null ? 'warn' : health >= 80 ? 'ok' : health < 50 ? 'danger' : 'warn';
    return {
      name: b.name || 'Battery',
      detail: join([
        b.manufacturer,
        b.chemistry,
        b.fullChargeCapacityMilliwattHours > 0 && b.designCapacityMilliwattHours > 0
          ? `${thousands(b.fullChargeCapacityMilliwattHours)} of ${thousands(
              b.designCapacityMilliwattHours
            )} mWh`
          : null,
        b.cycleCount > 0 ? `${b.cycleCount} cycles` : null,
        b.serialNumber ? `SN ${b.serialNumber}` : null
      ]),
      healthPercent: health,
      healthText: health === null ? '' : `${Math.round(health)}% health`,
      tone
    };
  });
}

export interface TpmState {
  readonly state: string;
  readonly detail: string;
  /**
   * Amber for both "absent" and "disabled in firmware": each is something to act on before this
   * machine can take Windows 11, and neither is a fault in the sense red is reserved for.
   */
  readonly tone: 'ok' | 'warn';
}

/**
 * The TPM, or null when the agent could not tell.
 *
 * THREE STATES, AND THE THIRD IS THE WHOLE DESIGN. `tpm` is null when the read was REFUSED - the TPM
 * lives in a namespace that turns away a non-administrator - and rendering that as "No TPM" would
 * put a Windows 11 blocker on a machine that has a working TPM 2.0. Null hides the section
 * entirely; only a real answer is ever displayed.
 */
export function tpmState(h: HardwareInfoDto | null): TpmState | null {
  const t: TpmDto | null | undefined = h?.tpm;
  if (!t) return null;
  if (!t.present) {
    return {
      state: 'Not detected',
      detail: 'This machine cannot run Windows 11 without a TPM 2.0.',
      tone: 'warn'
    };
  }
  const ready = t.enabled && t.activated;
  return {
    state: ready ? 'Ready' : 'Present but disabled in firmware',
    detail: join([
      t.specVersion ? `TPM ${t.specVersion}` : null,
      t.manufacturer,
      t.manufacturerVersion,
      t.owned ? 'owned' : null
    ]),
    tone: ready ? 'ok' : 'warn'
  };
}

/** "LG Electronics 17Z90TR (rev FAB1)". */
export function motherboardText(h: HardwareInfoDto | null): string {
  const b = h?.motherboard;
  if (!b) return '';
  const name = join([b.manufacturer, b.model], ' ');
  return b.version ? join([name, `(rev ${b.version})`], ' ') : name;
}

export function motherboardSerialText(h: HardwareInfoDto | null): string {
  const sn = h?.motherboard?.serialNumber;
  return sn ? `Serial ${sn}` : '';
}

/**
 * "Phoenix Technologies Ltd. A3ZJ3390 X64 · released 2025-07-16".
 *
 * The release date is a calendar date, so it is printed rather than run through a local-time
 * conversion that would move it a day for every reader west of Greenwich.
 */
export function biosText(h: HardwareInfoDto | null): string {
  if (!h) return '';
  const version = h.bios?.version || h.biosVersion;
  return join([
    join([h.bios?.vendor, version], ' '),
    h.bios?.releaseDateUtc ? `released ${plainDate(h.bios.releaseDateUtc)}` : null
  ]);
}

export function diskRows(h: HardwareInfoDto | null): HardwareRow[] {
  return (h?.disks ?? []).slice(0, MAX_HARDWARE_ROWS).map((d: DiskDto) => ({
    name: d.model,
    detail: join([
      d.sizeBytes > 0 ? bytes(d.sizeBytes) : null,
      d.mediaType,
      d.serialNumber ? `SN ${d.serialNumber}` : null
    ])
  }));
}

export function nicRows(h: HardwareInfoDto | null): HardwareRow[] {
  return (h?.nics ?? []).slice(0, MAX_HARDWARE_ROWS).map((n: NicDto) => ({
    name: n.description,
    detail: n.macAddress,
    // Collected since the first agent build and never shown until the hardware card was built -
    // which is why finding a machine's IP address meant opening a remote session to it.
    extra: (n.ipAddresses ?? []).join(', ')
  }));
}

/**
 * Whether this device runs the WINDOWS agent.
 *
 * Keyed off the OS caption's first word, which is a contract: the Synology agent's collector
 * guarantees its caption starts with "Synology " (or "Linux " for a generic box). A device with no
 * inventory yet, or any unrecognized caption, reads as Windows so every existing device keeps its
 * full page rather than silently losing tabs.
 */
export { isWindowsCaption } from './platform.js';

function round1(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}

function thousands(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
